import React, { FC, MouseEventHandler, ReactNode } from 'react';
import { PopoverOrigin, Popover } from '../Popover/Popover';
import { usePopover } from '../Popover/usePopover';
import { Tooltip } from '../Tooltip/Tooltip';
import { BpDesignSystem } from '../../types';
import { styled } from '../../styles';
import { Box } from '../Box';

interface ButtonItemProps {
  disabled?: boolean;
  active?: boolean;
  fullWidth?: boolean;
  open?: boolean;
}

export type DropdownProps = BpDesignSystem.PropsWithChildren<{
  tooltip?: string;
  children: ReactNode;
  ButtonItem(props: ButtonItemProps): ReactNode;
  closeOnSelect?: boolean;
  onClose?(): void;
  onOpen?(): void;
  disabled?: boolean;
  fullWidth?: boolean;
  active?: boolean;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
}>;

const nope = () => ({});

export const Dropdown: FC<DropdownProps> = ({
  tooltip = '',
  dataTest,
  disabled,
  active,
  fullWidth,
  closeOnSelect = true,
  ButtonItem,
  onClose,
  onOpen,
  anchorOrigin,
  transformOrigin,
  children,
  ...props
}) => {
  const popover = usePopover(onClose, onOpen);
  return (
    <>
      <Tooltip title={tooltip} disabled={disabled || !tooltip}>
        <StyledActiveWrapper component="span" $active={active} $fullWidth={fullWidth} {...props}>
          <span data-test={dataTest} role="region" onClick={!disabled ? popover.toggle : nope}>
            {ButtonItem({ disabled, active, fullWidth, open: popover.open })}
          </span>
        </StyledActiveWrapper>
      </Tooltip>
      <Popover
        open={popover.open}
        anchorEl={popover.anchorEl}
        onClose={popover.close}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
      >
        <div role="region" onClick={closeOnSelect ? (popover.close as MouseEventHandler<HTMLDivElement>) : nope}>
          {children}
        </div>
      </Popover>
    </>
  );
};

export default Dropdown;

const StyledActiveWrapper = styled(Box)<{ $active?: boolean; $fullWidth?: boolean }>`
  && {
    display: inline-block;
    vertical-align: middle;
    ${({ $fullWidth }) => ($fullWidth ? 'width: 100%;' : '')}

    path {
      fill: ${({ $active, theme }) => ($active ? theme.tokens.colorPrimary : undefined)} !important;
    }
  }
`;
